import { Reservation } from "shared/reservation"
import type { SstDoor } from "shared/sst"
import type { Sst2Door } from "shared/sst2"
import { isSst2ReservationVisible } from "../../sst2Status"

/**
 * Pair a door with everything known about what is inside it, on both sides of
 * the integration, for either partner API version.
 *
 * The two sides are keyed differently and neither is a superset of the other:
 *
 *  - V2 carries the partner reservation INLINE on the door
 *    (`door.reservations[]`), so the SST view exists even when SL2 never
 *    recorded a matching Thing — which is exactly the desync an admin is
 *    usually here to diagnose.
 *  - V1 carries nothing on the door but a `links` marker, so the partner
 *    payload can only be recovered from the SL2 Reservation's embedded
 *    `sstReservation` snapshot. Losing the match therefore empties BOTH
 *    columns at once, which is why a v1 mis-pair reads as "nothing here"
 *    rather than as a desync.
 *
 * ## LOCKER-355 — why `door.door_id` alone could never work
 *
 * This module used to key a reservation's door solely on
 * `sstReservation.door.door_id`. **That field is absent on every
 * partner-sourced v1 reservation row**, so the v1 branch compared `undefined`
 * against a 24-hex ObjectId and matched nothing, ever. The finding is not new
 * here — LOCKER-343 recorded it from production and named this file as the
 * consumer it broke:
 *
 *   > the door object embedded in a **v1 reservation** carries only `_id` —
 *   > `door_id` is absent, even though `SstDoorReservation` types both as
 *   > required. A *locker's* `sstLocker.doors[]` entry does carry `door_id`;
 *   > the two shapes differ and nothing in the type system says so. Result:
 *   > `undefined` on every v1 row, confirmed on 3/3 prod rows.
 *   > — `flipReservationToV2.ts`, LOCKER-343 / F1
 *
 * And the mirror image is just as real: a reservation MOBILUS minted against a
 * virtual v1 locker (`reservationApi.preCreate`) carries `door_id` and no
 * `_id`. So neither key is present across the whole v1 population, and reading
 * "`_id` instead" would simply move the blind spot. Both are accepted —
 * `sstDoorKeysOf` — which is what the backend has done in four places since
 * LOCKER-343 (`saLockerDoorRepair.ts`, `claimBackfilledV1Reservation.ts`,
 * `flipReservationToV2.ts`, `rollbackReservationFromV2.ts`).
 *
 * Accepting either key cannot mis-pair: SST v1 ids are 24-hex ObjectIds and v2
 * ids are uuids, so the two spaces cannot collide, and on every shape observed
 * so far a row carrying both holds the same value in each.
 *
 * ## Matching does NOT use `Reservation.doorId` — and the reason has changed
 *
 * This doc used to justify that by saying `doorId` "is the SL2 door Thing id, a
 * different id space". **That claim is wrong and was retracted in-tree on
 * 2026-07-30** (`shared/reservation.d.ts`): `doorId` holds the *SST* door id —
 * `reservationApi.preCreate` sets it from `availableDoor.door_id` and matches
 * `d.door_id === inThing.doorId`, and `claimBackfilledV1Reservation` writes the
 * partner v1 door id into it for the same reason.
 *
 * It stays unused anyway, deliberately: LOCKER-355 scopes the join to the
 * embedded snapshot, and once `sstDoorKeysOf` accepts both keys there is no
 * case left that `doorId` would rescue. Widening the join is a decision to take
 * on its own evidence, not a side effect of a pairing fix — so the constraint
 * is kept and the stale reason is corrected rather than left standing.
 */

export type SstApiVersion = "v1" | "v2"

export type DoorInspection = {
  /** The partner's door id — `door_id` on V1, `id` on V2. */
  doorKey: string
  /** Which partner API the LOCKER is on — the id space `doorKey` lives in. */
  lockerApiVersion: SstApiVersion
  /** Partner-side reservation payloads, newest-first is not guaranteed. */
  sstReservations: unknown[]
  /** SL2 Reservation Things pointing at this door. */
  sl2Reservations: Reservation[]
  /** SST reports a reservation that SL2 has no Thing for. */
  hasOrphanSstReservation: boolean
  /** SL2 has a Thing whose partner reservation is absent from the snapshot. */
  hasOrphanSl2Reservation: boolean
}

/**
 * The version of the payload THIS ROW carries, which is not always the
 * locker's. A locker can be `sstApiVersion: "v2"` while individual rows are
 * legitimately still `v1` — mid-migration, migration-failed, or rolled back —
 * and the reverse survives a locker-level rollback. `sstReservationVersion` is
 * the discriminant the rest of the wfe already branches on; falling back to the
 * locker's version is only for rows written before it existed.
 */
export const reservationVersionOf = (
  reservation: Reservation,
  lockerApiVersion: SstApiVersion,
): SstApiVersion => reservation.sstReservationVersion ?? lockerApiVersion

/**
 * Every partner door id this row's embedded snapshot names, under either key.
 *
 * Both, not one with a `??` fallback: the two keys are populated by different
 * writers and a row can legitimately carry either alone (see the module doc).
 * De-duplicated because the common case is that both are present and equal.
 */
export const sstDoorKeysOf = (reservation: Reservation): string[] => {
  const door = (
    reservation.sstReservation as
      | { door?: { door_id?: string; _id?: string } }
      | undefined
  )?.door
  const keys = [door?.door_id, door?._id].filter(
    (k): k is string => typeof k === "string" && k.length > 0,
  )
  return [...new Set(keys)]
}

/** The partner's reservation id carried by this row's embedded snapshot. */
export const sstIdOf = (reservation: Reservation): string | undefined =>
  (reservation.sstReservation as { _id?: string } | undefined)?._id

/**
 * Is this door occupied, by the SAME predicate the door tile renders from?
 *
 * Exported and shared so the occupancy percentage in the section header and the
 * tiles below it cannot drift apart — they were two independent expressions,
 * which is a disagreement waiting to happen rather than one that had happened.
 *
 *  - V1: the tile (`OsLockerDoor`) derives "occupied" from the `links`
 *    reservation marker; `!is_available` with no link is *disabled*, not
 *    occupied, and is excluded here for the same reason the tile prints
 *    "unavailable" for it.
 *  - V2: the tile (`OsLockerDoorV2`) filters the inline reservations through
 *    `isSst2ReservationVisible`, so a terminal reservation leaves the door free.
 */
export const isDoorOccupied = (args: {
  apiVersion: SstApiVersion
  door: SstDoor | Sst2Door
}): boolean => {
  const { apiVersion, door } = args
  if (apiVersion === "v2") {
    return ((door as Sst2Door).reservations || []).some(isSst2ReservationVisible)
  }
  return !!(door as SstDoor).links?.some(
    (l: { rel?: string }) => l?.rel === "reservation",
  )
}

/** The partner door id a door object answers to, in its own version's space. */
export const doorKeyOf = (args: {
  apiVersion: SstApiVersion
  door: SstDoor | Sst2Door
}): string =>
  args.apiVersion === "v2"
    ? (args.door as Sst2Door).id
    : (args.door as SstDoor).door_id

export const inspectDoor = (args: {
  apiVersion: SstApiVersion
  door: SstDoor | Sst2Door
  reservations: Reservation[]
}): DoorInspection => {
  const { apiVersion, door, reservations } = args

  // Only rows whose OWN payload version matches the locker's address the same
  // id space. A v1 row on a v2 locker names a v1 door id that no v2 door will
  // ever equal, so including it could only ever produce a false pair — it is
  // surfaced by `inspectLockerReservations` instead, which says out loud that
  // it cannot be placed.
  const sameVersion = reservations.filter(
    (r) => reservationVersionOf(r, apiVersion) === apiVersion,
  )

  if (apiVersion === "v2") {
    const d = door as Sst2Door
    const sstReservations = d.reservations || []
    const sstIds = new Set(sstReservations.map((r) => r.id))
    const sl2Reservations = sameVersion.filter((r) => {
      const id = sstIdOf(r)
      return sstDoorKeysOf(r).includes(d.id) || (!!id && sstIds.has(id))
    })
    const matchedIds = new Set(sl2Reservations.map(sstIdOf).filter(Boolean))
    return {
      doorKey: d.id,
      lockerApiVersion: apiVersion,
      sstReservations,
      sl2Reservations,
      hasOrphanSstReservation: sstReservations.some((r) => !matchedIds.has(r.id)),
      hasOrphanSl2Reservation: sl2Reservations.some((r) => {
        const id = sstIdOf(r)
        return !id || !sstIds.has(id)
      }),
    }
  }

  const d = door as SstDoor
  const sl2Reservations = sameVersion.filter((r) =>
    sstDoorKeysOf(r).includes(d.door_id),
  )
  const sstReservations = sl2Reservations
    .map((r) => r.sstReservation)
    .filter((r): r is NonNullable<typeof r> => !!r)
  // V1 exposes only a `links` marker, so a door SST believes is occupied while
  // SL2 holds nothing is detectable — but the reverse is not, since the partner
  // payload only ever reaches us through the SL2 Thing.
  //
  // LOCKER-355: now that the join works, "we hold a live row and the partner
  // says the door is free" IS computable on v1 for the first time. It stays
  // OFF here anyway. That condition is `SaLockerDoorRepairPanel`'s divergence
  // class F, which the panel classifies against a LIVE partner read; deriving a
  // second, weaker verdict from a possibly-stale locker snapshot would put two
  // answers on one drawer and make the badge mean something different on v1
  // than on v2. Reported on the ticket, not fixed here.
  const sstSaysOccupied = isDoorOccupied({ apiVersion, door: d })
  return {
    doorKey: d.door_id,
    lockerApiVersion: apiVersion,
    sstReservations,
    sl2Reservations,
    hasOrphanSstReservation: sstSaysOccupied && sl2Reservations.length === 0,
    hasOrphanSl2Reservation: false,
  }
}

export type LockerReservationPairing = {
  /**
   * Rows whose own `sstReservationVersion` differs from the locker's
   * `sstApiVersion`. A locker holding any of these is MIXED, and saying so is
   * the difference between "this door is empty" and "this door's record is
   * addressed in the other version's id space".
   */
  versionMismatched: Reservation[]
  /**
   * Rows that name no door in the snapshot at all — including same-version rows
   * whose embedded door was left describing the version they were rolled back
   * away from. Both rollback paths have a documented `no-door-source` branch
   * that produces exactly this and logs "the row needs a manual door repair";
   * before LOCKER-355 such a row was simply invisible.
   */
  unpairable: Reservation[]
}

/**
 * The locker-wide view of what could NOT be placed on a door, so an empty door
 * can explain itself instead of just being empty (LOCKER-355 §Desired
 * behaviour). Deliberately separate from `inspectDoor`, which stays per-door
 * and pure: an unplaceable row belongs to no door by definition, so attributing
 * it to whichever one happens to be open would be a fresh mis-pairing.
 */
export const inspectLockerReservations = (args: {
  apiVersion: SstApiVersion
  doors: (SstDoor | Sst2Door)[]
  reservations: Reservation[]
}): LockerReservationPairing => {
  const { apiVersion, doors, reservations } = args
  const doorKeys = new Set(doors.map((door) => doorKeyOf({ apiVersion, door })))
  const inlineSstIds = new Set(
    apiVersion === "v2"
      ? (doors as Sst2Door[]).flatMap((d) => (d.reservations || []).map((r) => r.id))
      : [],
  )

  const versionMismatched: Reservation[] = []
  const unpairable: Reservation[] = []

  reservations.forEach((r) => {
    if (reservationVersionOf(r, apiVersion) !== apiVersion) {
      versionMismatched.push(r)
      return
    }
    const id = sstIdOf(r)
    const placed =
      sstDoorKeysOf(r).some((k) => doorKeys.has(k)) || (!!id && inlineSstIds.has(id))
    if (!placed) unpairable.push(r)
  })

  return { versionMismatched, unpairable }
}
